import { RichEditor, isDocEmpty } from '@odo/shared-ui';
import type { RichEditorImageUploadHandler, TiptapDoc } from '@odo/shared-ui';
import type { TimelineEvent } from '../../../patient-files/actions/types';
import { useResolvedDxPayload } from './useResolvedDxPayload';

interface DxDraftPreviewProps {
    patientId: string;
    draft: TimelineEvent;
}

// Read-only preview only — RichEditor never invokes these while readOnly.
const NOOP_CHANGE = (): void => {};
const NOOP_UPLOAD: RichEditorImageUploadHandler = () => Promise.reject(new Error('read-only'));

interface ReadOnlyDocProps {
    doc: TiptapDoc;
    chrome?: 'card' | 'bare';
}

export function ReadOnlyDoc({ doc, chrome = 'card' }: ReadOnlyDocProps) {
    return (
        <RichEditor
            value={doc}
            onChange={NOOP_CHANGE}
            onImageUpload={NOOP_UPLOAD}
            readOnly
            chrome={chrome}
            minHeightPx={0}
            enableImageCaptions
        />
    );
}

export function DxDraftPreview({ patientId, draft }: DxDraftPreviewProps) {
    const { payload, isResolving } = useResolvedDxPayload(patientId, draft);

    if (isResolving && !payload) {
        return (
            <div className="px-1 py-3 flex items-center gap-2 text-xs text-gray-500">
                <span className="h-3 w-3 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
                Loading preview…
            </div>
        );
    }

    if (!payload) {
        return <p className="px-1 py-3 text-xs text-gray-500">This draft has no content yet.</p>;
    }

    const sections = payload.sections.filter((s) => !isDocEmpty(s.tiptapJson));
    if (sections.length === 0) {
        return <p className="px-1 py-3 text-xs text-gray-500">This draft has no content yet.</p>;
    }

    return (
        <div className="space-y-3 pt-1">
            {sections.map((section) => (
                <div key={section.id} className="space-y-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        {section.title}
                    </span>
                    <ReadOnlyDoc doc={section.tiptapJson} />
                </div>
            ))}
        </div>
    );
}
